/**
 * CSAT Dashboard — call sentiment + SMS survey results.
 * With sentiment filter + drill-down on call analysis.
 */
import React from 'react';
import { Smile, Frown, Meh, MessageSquare, TrendingUp, AlertTriangle, Phone, ChevronDown, ChevronUp, Ticket as TicketIcon, X } from 'lucide-react';
import { useGet } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { ErrorState } from '../../components/ui/EmptyState.js';

interface CsatData {
  callSentiment: { total: number; avgSentiment: number | null; alerts: number; breakdown: Record<string, number> };
  smsSurvey: { total: number; inbound: number; outbound: number; recent: Array<{ id: string; from: string; to: string; text: string; direction: string; createdAt: string; cwTicketId: number | null }> };
  recentAnalyses: Array<{
    callId: string; sentiment: number; alert: boolean; summary: string;
    issue: string | null; stepsTaken: string | null; resolution: string | null; nextSteps: string | null;
    callerNumber: string | null; cwTicketId: number | null; createdAt: string;
  }>;
}

const SENTIMENT_ICONS: Record<number, React.ElementType> = { 1: Frown, 2: Frown, 3: Meh, 4: Smile, 5: Smile };
const SENTIMENT_COLORS: Record<number, string> = { 1: 'text-red-500', 2: 'text-orange-500', 3: 'text-yellow-500', 4: 'text-green-500', 5: 'text-green-600' };
const SENTIMENT_BAR: Record<number, string> = { 1: 'bg-red-500', 2: 'bg-orange-500', 3: 'bg-yellow-500', 4: 'bg-green-500', 5: 'bg-green-600' };

type FilterValue = 'all' | '1' | '2' | '3' | '4' | '5' | 'alerts';

export function CsatPage() {
  const { data, loading, error, refetch } = useGet<CsatData>('/api/v1/calls/csat?days=30');
  const [filter, setFilter] = React.useState<FilterValue>('all');
  const [expandedCall, setExpandedCall] = React.useState<string | null>(null);

  if (loading) return <div className="space-y-4"><CardSkeleton lines={3} /><CardSkeleton lines={3} /></div>;
  if (error) return <ErrorState message={error} onRetry={refetch} />;
  if (!data) return null;

  const AvgIcon = data.callSentiment.avgSentiment ? SENTIMENT_ICONS[Math.round(data.callSentiment.avgSentiment)] ?? Meh : Meh;
  const avgColor = data.callSentiment.avgSentiment ? SENTIMENT_COLORS[Math.round(data.callSentiment.avgSentiment)] ?? 'text-yellow-500' : 'text-yellow-500';

  const filteredAnalyses = data.recentAnalyses.filter((a) => {
    if (filter === 'all') return true;
    if (filter === 'alerts') return a.alert;
    return a.sentiment === Number(filter);
  });

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">CSAT Dashboard</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))]">Call sentiment and SMS survey results (last 30 days)</p>
        </div>
        {/* Sentiment filter */}
        <select value={filter} onChange={(e) => setFilter(e.target.value as FilterValue)}
          className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] focus:outline-none focus:ring-2 focus:ring-brand-500">
          <option value="all">All Sentiments</option>
          <option value="alerts">⚠ Alerts Only</option>
          <option value="5">5 — Excellent</option>
          <option value="4">4 — Good</option>
          <option value="3">3 — Neutral</option>
          <option value="2">2 — Poor</option>
          <option value="1">1 — Bad</option>
        </select>
      </header>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5">
          <div className="flex items-center gap-3">
            <AvgIcon className={"h-8 w-8 " + avgColor} />
            <div>
              <p className="text-xs text-[hsl(var(--content-subtle))]">Avg Sentiment</p>
              <p className={"text-2xl font-bold " + avgColor}>{data.callSentiment.avgSentiment ? data.callSentiment.avgSentiment.toFixed(1) + "/5" : "N/A"}</p>
            </div>
          </div>
          <p className="text-xs text-[hsl(var(--content-subtle))] mt-2">{data.callSentiment.total} calls analyzed</p>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5">
          <div className="flex items-center gap-3">
            <AlertTriangle className={"h-8 w-8 " + (data.callSentiment.alerts > 0 ? "text-red-500" : "text-green-500")} />
            <div>
              <p className="text-xs text-[hsl(var(--content-subtle))]">Sentiment Alerts</p>
              <p className={"text-2xl font-bold " + (data.callSentiment.alerts > 0 ? "text-red-500" : "text-green-500")}>{data.callSentiment.alerts}</p>
            </div>
          </div>
          <p className="text-xs text-[hsl(var(--content-subtle))] mt-2">Calls flagged for negative sentiment</p>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-brand-500" />
            <div>
              <p className="text-xs text-[hsl(var(--content-subtle))]">SMS Messages</p>
              <p className="text-2xl font-bold text-[hsl(var(--content))]">{data.smsSurvey.total}</p>
            </div>
          </div>
          <p className="text-xs text-[hsl(var(--content-subtle))] mt-2">{data.smsSurvey.inbound} inbound · {data.smsSurvey.outbound} outbound</p>
        </div>
      </div>

      {/* Sentiment breakdown */}
      {Object.keys(data.callSentiment.breakdown).length > 0 && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-4">Sentiment Breakdown</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((score) => {
              const count = data.callSentiment.breakdown[String(score)] ?? 0;
              const pct = data.callSentiment.total > 0 ? (count / data.callSentiment.total) * 100 : 0;
              const Icon = SENTIMENT_ICONS[score] ?? Meh;
              const color = SENTIMENT_COLORS[score] ?? 'text-yellow-500';
              const isActive = filter === String(score);
              return (
                <button key={score} type="button" onClick={() => setFilter(isActive ? 'all' : String(score) as FilterValue)}
                  className={"flex items-center gap-3 w-full text-left rounded-lg px-2 py-1 transition-colors " + (isActive ? "bg-brand-50 dark:bg-brand-950/30" : "hover:bg-[hsl(var(--surface-subtle))]")}>
                  <Icon className={"h-5 w-5 " + color + " shrink-0"} />
                  <span className="text-sm text-[hsl(var(--content-subtle))] w-8">{score}/5</span>
                  <div className="flex-1 h-6 rounded-full bg-[hsl(var(--surface-subtle))] overflow-hidden">
                    <div className={"h-full rounded-full " + (SENTIMENT_BAR[score] ?? 'bg-yellow-500')} style={{ width: pct + '%' }} />
                  </div>
                  <span className="text-sm font-medium text-[hsl(var(--content))] w-8 text-right">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Call analyses with drill-down */}
      {filteredAnalyses.length > 0 && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-3">
            Call Analyses {filter !== 'all' && <span className="text-[hsl(var(--content-subtle))]">({filteredAnalyses.length} filtered)</span>}
          </h3>
          <ul className="space-y-2">
            {filteredAnalyses.map((a) => {
              const Icon = SENTIMENT_ICONS[a.sentiment] ?? Meh;
              const color = SENTIMENT_COLORS[a.sentiment] ?? 'text-yellow-500';
              const isExpanded = expandedCall === a.callId;
              return (
                <li key={a.callId} className="rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                  <button type="button" onClick={() => setExpandedCall(isExpanded ? null : a.callId)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                    <Icon className={"h-5 w-5 " + color + " shrink-0"} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[hsl(var(--content))] truncate">{a.summary}</p>
                      <p className="text-xs text-[hsl(var(--content-subtle))]">
                        {new Date(a.createdAt).toLocaleString()} · Sentiment: {a.sentiment}/5{a.alert ? " · ⚠ ALERT" : ""}
                        {a.callerNumber && " · 📞 " + a.callerNumber}
                        {a.cwTicketId && " · #" + a.cwTicketId}
                      </p>
                    </div>
                    {a.alert && <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />}
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" /> : <ChevronDown className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] space-y-3">
                      {a.issue && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))] mb-1">Issue</p>
                          <p className="text-sm text-[hsl(var(--content))]">{a.issue}</p>
                        </div>
                      )}
                      {a.stepsTaken && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))] mb-1">Steps Taken</p>
                          <p className="text-sm text-[hsl(var(--content))]">{a.stepsTaken}</p>
                        </div>
                      )}
                      {a.resolution && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))] mb-1">Resolution</p>
                          <p className="text-sm text-[hsl(var(--content))]">{a.resolution}</p>
                        </div>
                      )}
                      {a.nextSteps && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))] mb-1">Next Steps</p>
                          <p className="text-sm text-[hsl(var(--content))]">{a.nextSteps}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-4 pt-2 border-t border-[hsl(var(--border))]">
                        {a.callerNumber && (
                          <span className="flex items-center gap-1 text-xs text-[hsl(var(--content-subtle))]"><Phone className="h-3 w-3" /> {a.callerNumber}</span>
                        )}
                        {a.cwTicketId && (
                          <span className="flex items-center gap-1 text-xs text-brand-600"><TicketIcon className="h-3 w-3" /> Ticket #{a.cwTicketId}</span>
                        )}
                        <span className={"text-xs font-medium " + color}>Sentiment: {a.sentiment}/5</span>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Recent SMS messages */}
      {data.smsSurvey.recent.length > 0 && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-3">Recent SMS Messages</h3>
          <ul className="space-y-2">
            {data.smsSurvey.recent.map((m, i) => (
              <li key={i} className="flex items-start gap-3 rounded-lg border border-[hsl(var(--border))] px-3 py-2">
                <div className={"flex h-8 w-8 items-center justify-center rounded-full shrink-0 " + (m.direction === "inbound" ? "bg-green-100 dark:bg-green-950/30" : "bg-blue-100 dark:bg-blue-950/30")}>
                  {m.direction === "inbound" ? <Phone className="h-4 w-4 text-green-600" /> : <MessageSquare className="h-4 w-4 text-blue-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[hsl(var(--content))]">{m.text}</p>
                  <p className="text-xs text-[hsl(var(--content-subtle))]">
                    {m.direction === "inbound" ? "From " + m.from : "To " + m.to} · {new Date(m.createdAt).toLocaleString()}
                    {m.cwTicketId && " · #" + m.cwTicketId}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.callSentiment.total === 0 && data.smsSurvey.total === 0 && (
        <div className="text-center py-12">
          <TrendingUp className="h-12 w-12 text-[hsl(var(--content-subtle))] mx-auto mb-3" />
          <p className="text-sm text-[hsl(var(--content-subtle))]">No CSAT data yet. Connect 3CX + Telnyx to start collecting call sentiment and SMS survey results.</p>
        </div>
      )}
    </div>
  );
}

